import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import type { BusinessResponse, ContentTopic } from "api/config/chalice-types";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { useCategories } from "hooks/useCategoryCRUD";
import PageContainer from "layout/PageContainer";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { currentBusinessGetter } from "store/user/userSlice";
import { CategoryList } from "./components/CategoryList";
import { HeaderActions } from "./components/HeaderActions";
import { TopicListItem } from "./components/TopicListItem";
import { TopicsPageModals } from "./components/TopicsPageModals";
import { useBulkEdit } from "./hooks/useBulkEdit";
import { useTopicDeduplication } from "./hooks/useTopicDeduplication";
import { useTopicDragAndDrop } from "./hooks/useTopicDragAndDrop";
import { useTopicOrganization } from "./hooks/useTopicOrganization";
import { useTopicsPageActions } from "./hooks/useTopicsPageActions";
import { useTopicsPageModals } from "./hooks/useTopicsPageModals";

// Helper to generate a unique key for a topic based on its content
const generateTopicKey = (topic: ContentTopic): string => {
  const categoryId = topic.category_id ?? "null";
  return `topic-${topic.title}-${topic.body}-${categoryId}-${topic.enabled}`;
};

const MEDIUM_BREAKPOINT = 768; // Define breakpoint for responsiveness

const TopicsPage = () => {
  const dispatch = useAppDispatch();
  const currentBusiness = useAppSelector(
    currentBusinessGetter
  ) as BusinessResponse | null;
  const { hasPermission } = useUserPermissions();
  const canEditBusiness = hasPermission("business_details_write");
  const canGeneratePosts = hasPermission("post_status_generate_write");
  const canDeleteTopics = hasPermission("business_details_write");

  const { categories, isLoadingCategories } = useCategories();

  const [displayTopics, setDisplayTopics] = useState<ContentTopic[]>([]);

  const serverTopics = useMemo(
    () => currentBusiness?.topics || [],
    [currentBusiness?.topics]
  );

  useTopicDeduplication({
    serverTopics,
    currentBusinessId: currentBusiness?.id,
    dispatch,
    setDisplayTopics,
  });

  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth < MEDIUM_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < MEDIUM_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { topicsByCategory, sortedCategoryNames, categoryIdToNameMap } =
    useTopicOrganization({
      topics: displayTopics,
      categories: isLoadingCategories ? [] : categories,
    });

  const {
    showModal,
    showCategoryModal,
    categoryName,
    setCategoryName,
    openNewTopicModal,
    openEditTopicModal,
    openGeneratePostsModal,
    openImportTopicsModal,
    openNewCategoryModal,
    openEditCategoryModal,
    closeModal,
    closeCategoryModal,
    categoryToDeleteConfirm,
    openCategoryDeleteConfirmModal,
    closeCategoryDeleteConfirmModal,
  } = useTopicsPageModals();

  const existingCategoryColors = useMemo(() => {
    return categories
      .map((category) => category.color)
      .filter((color): color is string => !!color);
  }, [categories]);

  const {
    isBulkEditMode,
    selectedTopicKeys,
    handleToggleBulkEditMode,
    handleTopicSelect,
    handleBulkDelete,
  } = useBulkEdit({
    displayTopics,
    dispatch,
    currentBusinessId: currentBusiness?.id,
    generateTopicKey,
    canDeleteTopics,
  });

  const {
    handleUpdateTopics,
    handleGeneratePostsForTopic,
    handleCategorySubmit,
    handleMoveCategoryTopicsToUncategorized,
    handleDeleteCategoryAndAllTopics,
    handleTopicListItemAction,
    handleCategorySectionAction,
  } = useTopicsPageActions({
    displayTopics,
    setDisplayTopics,
    serverTopics,
    categories,
    categoryName,
    currentCategoryModalData: showCategoryModal,
    closeCategoryModal,
    closeModal,
    isBulkEditMode,
    generateTopicKey,
    openEditTopicModal,
    openGeneratePostsModal,
    openCategoryDeleteConfirmModal,
    openEditCategoryModal,
  });

  const {
    activeItem,
    activeCategoryKeys,
    handleCollapseChange,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useTopicDragAndDrop({
    topics: displayTopics,
    categories,
    sortedCategoryNames,
    categoryIdToNameMap,
    currentBusiness,
  });

  return (
    <>
      <PageContainer
        title="Content Topics"
        subtitle={
          isBulkEditMode
            ? `Select topics to delete.`
            : "Organize your content ideas into categories."
        }
        headerActions={
          <HeaderActions
            isBulkEditMode={isBulkEditMode}
            isSmallScreen={isSmallScreen}
            selectedTopicCount={selectedTopicKeys.size}
            onToggleBulkEditMode={handleToggleBulkEditMode}
            onBulkDelete={handleBulkDelete}
            onImportTopics={openImportTopicsModal}
            onNewTopic={() => openNewTopicModal(null)}
            onNewCategory={openNewCategoryModal}
            canEditBusiness={canEditBusiness}
          />
        }
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <main className="space-y-6">
            <CategoryList
              isLoadingCategories={isLoadingCategories}
              sortedCategoryNames={sortedCategoryNames}
              topicsByCategory={topicsByCategory}
              categories={categories}
              isBulkEditMode={isBulkEditMode}
              canEditBusiness={canEditBusiness}
              canGeneratePosts={canGeneratePosts}
              handleTopicListItemAction={handleTopicListItemAction}
              handleCategorySectionAction={handleCategorySectionAction}
              openNewTopicModal={openNewTopicModal}
              activeCategoryKeys={activeCategoryKeys}
              handleCollapseChange={handleCollapseChange}
              selectedTopicKeys={selectedTopicKeys}
              handleTopicSelect={handleTopicSelect}
              generateTopicKey={generateTopicKey}
            />
          </main>
          {!isBulkEditMode && (
            <DragOverlay dropAnimation={null}>
              {activeItem ? (
                <TopicListItem
                  key={`drag-overlay-${generateTopicKey(activeItem)}`}
                  id={generateTopicKey(activeItem)}
                  topic={activeItem}
                  canEditBusiness={true}
                  canGeneratePosts={false}
                  onTopicAction={() => {}}
                  isBulkEditMode={false}
                  isSelected={false}
                  onSelect={() => {}}
                />
              ) : null}
            </DragOverlay>
          )}
        </DndContext>
      </PageContainer>

      <TopicsPageModals
        isBulkEditMode={isBulkEditMode}
        showModal={showModal}
        showCategoryModal={showCategoryModal}
        categoryToDeleteConfirm={categoryToDeleteConfirm}
        categoryName={categoryName}
        displayTopics={displayTopics}
        categories={categories}
        existingCategoryColors={existingCategoryColors}
        generateTopicKey={generateTopicKey}
        handleGeneratePostsForTopic={handleGeneratePostsForTopic}
        handleUpdateTopics={handleUpdateTopics}
        handleCategorySubmit={handleCategorySubmit}
        handleMoveCategoryTopicsToUncategorized={
          handleMoveCategoryTopicsToUncategorized
        }
        handleDeleteCategoryAndAllTopics={handleDeleteCategoryAndAllTopics}
        closeModal={closeModal}
        closeCategoryModal={closeCategoryModal}
        closeCategoryDeleteConfirmModal={closeCategoryDeleteConfirmModal}
        setCategoryName={setCategoryName}
      />
    </>
  );
};

export default TopicsPage;
